/**
 * The corner motifs a studio may choose between — Document 6 §6.2.
 *
 * Revises D-07, which said one shared ornament set and no choice. The reason
 * D-07 gave still holds and is why this is a list of five rather than an open
 * one: the cost to avoid is commissioning per-palette artwork, and none of
 * these carry colour of their own. They are drawn with `currentColor` and
 * tinted by whichever palette the studio picked, exactly as the single
 * ornament was.
 *
 * What changed is that a studio's report is the studio's, and a Pilates studio,
 * a reformer studio and a rehabilitation practice do not want the same mark in
 * the corner of a document they hand to a client. Asked for 10 September 2026.
 *
 * The paths live in the report template; this file is the vocabulary both
 * sides agree on, so the settings screen and the renderer cannot disagree
 * about which motifs exist.
 */

export const ORNAMENT_KEYS = ['yaprak', 'dalga', 'ay', 'ark', 'yok'] as const;

export type OrnamentKey = (typeof ORNAMENT_KEYS)[number];

/** What every studio had before there was a choice. */
export const DEFAULT_ORNAMENT_KEY: OrnamentKey = 'yaprak';

export interface OrnamentDefinition {
  key: OrnamentKey;
  /** Shown in studio settings. */
  name: string;
  /** One line on what it suits, for an owner choosing without a preview. */
  description: string;
}

export const ORNAMENTS: Record<OrnamentKey, OrnamentDefinition> = {
  yaprak: {
    key: 'yaprak',
    name: 'Yaprak',
    description: 'Botanik ve yumuşak. Varsayılan.',
  },
  dalga: {
    key: 'dalga',
    name: 'Dalga',
    description: 'Akışkan çizgiler, hareketi çağrıştırır.',
  },
  ay: {
    key: 'ay',
    name: 'Ay',
    description: 'Sade daireler, sakin ve modern.',
  },
  ark: {
    key: 'ark',
    name: 'Kemer',
    description: 'Geometrik yaylar, mimari bir his.',
  },
  yok: {
    key: 'yok',
    name: 'Motif Yok',
    description: 'Tamamen sade sayfa. Süs istemeyenler için.',
  },
};

export function isOrnamentKey(value: unknown): value is OrnamentKey {
  return (
    typeof value === 'string' && (ORNAMENT_KEYS as readonly string[]).includes(value)
  );
}

/** Falls back rather than throwing: an unknown key is a report, not an error. */
export function getOrnamentKey(value: unknown): OrnamentKey {
  return isOrnamentKey(value) ? value : DEFAULT_ORNAMENT_KEY;
}
